/**
 * 页面可见性处理模块（简化版）
 * 核心思路：页面可见时直接复用 fnJoinTheHall 初始化逻辑
 */

import { stopAllCardAnimations, isPageVisible } from './cardAnimation';

// 长时间离开阈值（5分钟）
const LONG_ABSENCE_THRESHOLD = 5 * 60 * 1000;

// 页面隐藏时间记录
let pageHiddenTime = 0;

// 🎯 会话版本号：每次页面可见性变化时递增，用于中止旧的异步操作
export let visibilitySessionId = 0;

/**
 * 创建页面可见性处理器（简化版）
 * @param context 上下文对象，包含必要的函数和状态
 */
export const createVisibilityHandler = (context: {
  oMusic: any;
  gameAudio?: { stopAll: () => void } | null;  // 游戏音效管理器
  gameTimers: any;
  clearHistoryBetTimers: () => void;
  cleanupRoomResources?: (silent: boolean) => Promise<void>;
  fnJoinTheHall: () => void;
  // 清空数据的回调
  clearPageData?: () => void;
  // 声音设置（用于判断是否静音）
  settingStore?: any;
  // 🔧 重置加入房间标志的回调（页面隐藏时调用，确保下次可见时能重新加入）
  resetJoiningFlag?: () => void;
  // 🎯 停止筹码结算动画（页面隐藏时调用）
  cleanupSettlementChips?: () => void;
  // 🛡️ 清空动画队列（页面隐藏时调用，防止恢复后执行旧任务）
  clearAnimationQueue?: () => void;
  // 🧹 清除所有筹码DOM（页面隐藏时调用，确保恢复时无旧筹码干扰）
  clearDOMChips?: () => void;
}) => {
  return () => {
    const isVisible = document.visibilityState === 'visible';
    
    // 🎯 关键：同步更新 isPageVisible 状态，确保事件监听器的保护检查生效
    isPageVisible.value = isVisible;

    if (isVisible) {
      // ========== 页面恢复可见 ==========
      console.log('[可见性] 页面恢复可见');
      
      // 1. 检测长时间离开
      const absenceTime = pageHiddenTime > 0 ? Date.now() - pageHiddenTime : 0;
      const isLongAbsence = absenceTime > LONG_ABSENCE_THRESHOLD;
      pageHiddenTime = 0;
      
      if (isLongAbsence) {
        console.log(`[可见性] 长时间离开（${(absenceTime / 1000 / 60).toFixed(1)}分钟），执行完全刷新`);
        
        // 递增会话版本号（确保旧的异步操作失效）
        visibilitySessionId++;
        
        if (context.cleanupRoomResources) {
          // 使用 cleanupRoomResources 清理并重新加入房间
          // rejoinRoom=true 会在清理完成后调用 fnJoinTheHall
          context.cleanupRoomResources(true).catch((err) => {
            console.error('[可见性] cleanupRoomResources 失败:', err);
            // 失败时回退到页面刷新
            window.location.reload();
          });
        } else {
          window.location.reload();
        }
        return;
      }
      
      // 2. 递增会话版本号（关键：使旧的异步操作失效）
      visibilitySessionId++;
      console.log('[可见性] 会话版本号递增:', visibilitySessionId);
      
      // 3. 清理旧状态
      stopAllCardAnimations();
      context.clearHistoryBetTimers();
      
      // 🔧 修复：页面可见时先清空旧数据，再拉取新数据，避免新旧数据混淆
      if (context.clearPageData) {
        context.clearPageData();
      }
      
      // 4. 恢复音乐（需检查用户声音设置）
      if (context.oMusic && typeof window !== 'undefined') {
        try {
          // 🎯 检查用户声音设置：只有在声音开启时才恢复BGM
          const isSoundOn = context.settingStore?.inhouseGames?.sound?.on !== false;
          const isMusicOn = context.settingStore?.inhouseGames?.sound?.music !== false;
          
          Object.values(context.oMusic.oAudio).forEach((audio: any) => {
            if (audio.isBGM) {
              // 只有在声音和音乐都开启时才取消静音并播放
              if (isSoundOn && isMusicOn) {
                audio.mute(false);
                if (!audio.playing()) audio.toPlay();
              }
            }
          });
        } catch (e) { /* ignore */ }
      }
    
      
      // 6. 直接复用 fnJoinTheHall 重新初始化游戏（服务器返回最新数据）
      context.fnJoinTheHall();
      
    } else {
      // ========== 页面隐藏 ==========
      console.log('[可见性] 页面隐藏');
      
      // 🔧 关键优化：页面隐藏时递增 sessionId，使所有正在进行的异步操作立即失效
      // 这样即使 fnJoinTheHall 正在执行，其后续回调也会因 sessionId 不匹配而中止
      visibilitySessionId++;
      console.log('[可见性] 页面隐藏，会话版本号递增:', visibilitySessionId);
      
      // 🔧 重置加入房间标志，确保下次可见时能重新加入
      if (context.resetJoiningFlag) {
        context.resetJoiningFlag();
      }
      
      // 1. 记录隐藏时间
      pageHiddenTime = Date.now();
      
      // 2. 停止所有动画
      stopAllCardAnimations();
      
      // 🎯 停止筹码结算动画
      if (context.cleanupSettlementChips) {
        context.cleanupSettlementChips();
        console.log('[可见性] 已停止筹码结算动画');
      }
      
      // 🛡️ 清空动画队列，防止页面恢复后执行旧的下注动画
      if (context.clearAnimationQueue) {
        context.clearAnimationQueue();
      }
      
      // 🧹 清除所有筹码DOM，确保页面恢复时无旧筹码干扰
      if (context.clearDOMChips) {
        context.clearDOMChips();
        console.log('[可见性] 已清除所有筹码DOM');
      }
      
      // 3. 静音BGM
      if (context.oMusic && typeof window !== 'undefined') {
        try {
          Object.values(context.oMusic.oAudio).forEach((audio: any) => {
            if (audio.isBGM) audio.mute(true);
          });
        } catch (e) { /* ignore */ }
      }
      
      // 4. 停止游戏音效（牛型播报等）
      if (context.gameAudio) {
        try {
          context.gameAudio.stopAll();
        } catch (e) { /* ignore */ }
      }
      
      // 5. 清理定时器
      if (context.gameTimers?.globalTimer) {
        clearInterval(context.gameTimers.globalTimer);
        context.gameTimers.globalTimer = null;
      }
      context.clearHistoryBetTimers();
      
      // 🔧 优化：页面隐藏时不清空数据！
      // 原因：如果在 fnJoinTheHall 异步执行期间清空数据，会导致状态混乱
      // 数据清理统一在页面可见时执行（clearPageData 在 visible 分支中调用）
      // 这样确保：清理 → 初始化 总是成对执行，不会被打断
    }
  };
};


// 获取当前会话版本号
export const getVisibilitySessionId = () => visibilitySessionId;

export default {
  createVisibilityHandler,
  visibilitySessionId,
  getVisibilitySessionId,
};
